/**
 * Sanitizers — the last thing that touches a value before it goes on the wire.
 *
 * The dashboard contract forbids putting message bodies, form field values,
 * email addresses, full URLs with query strings, or precise geolocation into
 * the log sink. The backend holds the same line (it records message
 * *lengths*, never message contents). Enforcing that at each call site would
 * mean trusting every future caller to remember; enforcing it here means a
 * careless call site produces a redacted event instead of a leak.
 *
 * These functions are total: every input yields a safe output, never a throw.
 *
 * @module logging/core/sanitize
 */

import type { ParamValue } from './types';

// =============================================================================
// BOUNDS
// =============================================================================

/** Longest string that reaches the wire. Cloud Logging truncates far later,
 *  but a bounded field cannot become an accidental channel for a stack trace
 *  that happens to quote user input. */
const MAX_STRING_LENGTH = 256;

/** Longest path retained. Real paths on this site are well under this. */
const MAX_PATH_LENGTH = 128;

/** Most params one event may carry. A caller spreading an API response into
 *  an event is a mistake this bound turns into a truncation rather than a
 *  schema explosion in the BigQuery sink. */
const MAX_PARAM_COUNT = 16;

// =============================================================================
// REDACTION
// =============================================================================

/** Anything shaped like an address. Applied before the opaque-run pass,
 *  which would otherwise eat the local part and leave a bare domain. */
const EMAIL_SHAPED = /[\w.+-]+@[\w-]+\.[\w.-]+/g;

/** Query strings and fragments on any URL appearing inside free text. */
const URL_TAIL = /(https?:\/\/[^\s?#]+)[?#][^\s]*/g;

/** Long opaque runs. The 24-character floor sits above ordinary English
 *  words and identifiers, and the character class excludes whitespace so
 *  this cannot swallow a sentence. */
const OPAQUE_RUN = /\b[A-Za-z0-9_-]{24,}\b/g;

/**
 * Shared redaction pass. Order matters: addresses, then URL tails, then
 * bare opaque runs.
 *
 * `opaqueRuns` is opt-in because the opaque-run rule is the only one here
 * that cannot tell a secret from an identifier. An address and a URL tail
 * have reliable signatures; a 24-character run is just a long token, and a
 * declared identifier field is *supposed* to carry one.
 */
function redact(value: string, opaqueRuns: boolean): string {
  const cleaned = value.replace(EMAIL_SHAPED, '[redacted]').replace(URL_TAIL, '$1');
  return opaqueRuns ? cleaned.replace(OPAQUE_RUN, '[redacted]') : cleaned;
}

/**
 * Redact and bound a free-text string.
 *
 * Used on anything a human or an exception wrote: error messages, and any
 * string param a caller supplies.
 */
export function sanitizeText(value: string, maxLength = MAX_STRING_LENGTH): string {
  const clean = redact(value, true).trim();
  return clean.length > maxLength ? `${clean.slice(0, maxLength - 1)}…` : clean;
}

/**
 * Redact and bound a declared identifier.
 *
 * Same bounds and the same address / URL redaction as free text, but without
 * the opaque-run pass — because on an identifier field that pass destroys
 * exactly the value the field exists to carry, and does it silently.
 *
 * The field this protects is `deck_recipient`. It is the one identity value
 * this site emits, and the entire reason a pitch deck is instrumented is to
 * answer "did this investor open it?". A watermark of 24 unbroken word
 * characters would have been replaced with `[redacted]`; the row still
 * lands, the column is still populated, and the question is unanswerable
 * forever. Today's nine watermarks are all human names whose spaces break
 * the run, so this was latent rather than live — which is the only reason
 * it was still here to find. `cta_id` sits behind it at 22 characters for
 * `deletion_request_email`, two characters short of the same fate.
 *
 * Found because the mobile client ported this module's regex and it was
 * already destroying live UUID join keys there.
 */
export function sanitizeIdentifier(
  value: string,
  maxLength = MAX_STRING_LENGTH,
): string {
  const clean = redact(value, false).trim();
  return clean.length > maxLength ? `${clean.slice(0, maxLength - 1)}…` : clean;
}

// =============================================================================
// LOCATION
// =============================================================================

/**
 * Reduce a URL or path to a safe pathname.
 *
 * Dropping the query string is the obvious half: it carries the `?capture=1`
 * test flag and UTM parameters today, and would carry user input the moment
 * anyone adds a form that navigates.
 *
 * The redaction pass is the non-obvious half, and it is not defensive
 * padding. `ROUTES.NOT_FOUND` is a `*` catch-all, so *any* URL a visitor
 * lands on renders a page and reports its own path — including a link
 * someone pastes with an address or a one-time identifier sitting in a path
 * segment rather than the query. Stripping `?` alone would not save us there.
 */
export function sanitizePath(input: string): string {
  // `new URL` needs a base for relative inputs and throws on genuinely
  // malformed ones. Both branches end at a usable pathname.
  let pathname: string;
  try {
    pathname = new URL(input, 'https://homeceo.ai').pathname;
  } catch {
    pathname = input.split(/[?#]/)[0] || '/';
  }
  const clean = redact(pathname, true).slice(0, MAX_PATH_LENGTH);
  return clean.startsWith('/') ? clean : `/${clean}`;
}

/**
 * Reduce a referrer to its host.
 *
 * The dashboard contract asks for host only: `google.com`, not the search
 * URL carrying the phrase the visitor typed. Same-origin navigation reports
 * as `internal` so it cannot inflate the acquisition charts, and an absent
 * referrer reports as `direct` rather than being omitted — a present-but-
 * constant column is easier to chart than a NULL.
 */
export function sanitizeReferrer(raw: string, currentHost: string): string {
  if (!raw) return 'direct';
  try {
    const { hostname } = new URL(raw);
    if (!hostname) return 'direct';
    return hostname === currentHost ? 'internal' : hostname.slice(0, MAX_STRING_LENGTH);
  } catch {
    return 'direct';
  }
}

// =============================================================================
// PARAMS
// =============================================================================

/**
 * Coerce a caller-supplied payload into bounded, wire-safe values.
 *
 * Runs *after* schema validation, not instead of it: the catalog decides
 * whether a field is allowed at all, this decides whether its value is safe
 * to ship. Anything that is not a string, finite number, or boolean is
 * dropped silently — throwing here would let a bad call site break a page.
 */
export function sanitizeParams(
  params: Readonly<Record<string, unknown>>,
  freeTextKeys: readonly string[] = [],
): Record<string, ParamValue> {
  const safe: Record<string, ParamValue> = {};
  const isFreeText = new Set(freeTextKeys);

  for (const [key, value] of Object.entries(params)) {
    if (Object.keys(safe).length >= MAX_PARAM_COUNT) break;

    if (typeof value === 'string') {
      /* Default is the identifier treatment. A field only gets the
         opaque-run pass by being declared `freeText` in the catalog, so a
         new field is under-redacted never by accident — an unlisted field
         is a call-site constant or an enum, which is what every param in
         this catalog except `message` actually is. */
      safe[key] = isFreeText.has(key) ? sanitizeText(value) : sanitizeIdentifier(value);
    } else if (typeof value === 'number') {
      // NaN and Infinity serialize to `null` and land as an untyped column;
      // a dropped field is the honest alternative.
      if (Number.isFinite(value)) safe[key] = value;
    } else if (typeof value === 'boolean') {
      safe[key] = value;
    }
  }

  return safe;
}
